import React, { useState } from 'react';
import { TextInput } from '@mantine/core';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLang: 'en' | 'fr' | 'de' | 'it';
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, currentLang }) => {
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [step, setStep] = useState<'email' | 'pin' | 'success'>('email');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { sendOTP, verifyOTP } = useAuth();

  const translations = {
    en: {
      title: 'Sign In',
      titlePin: 'Enter PIN Code',
      titleSuccess: 'Welcome!',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      emailDescription: 'We\'ll send you a 6-digit PIN code to sign in',
      pin: 'PIN Code',
      pinPlaceholder: '000000',
      pinDescription: 'Enter the 6-digit code from your email',
      sentMessage: 'We sent a 6-digit PIN to',
      successMessage: 'Successfully signed in',
      cancel: 'Cancel',
      back: 'Back',
      sendCode: 'Send Code',
      verify: 'Verify',
      resend: 'Resend Code',
      errorRateLimit: 'Too many requests. Please wait a minute before trying again.',
      errorInvalid: 'Please enter a valid email address.',
      errorInvalidPin: 'Invalid PIN code. Please try again.',
    },
    fr: {
      title: 'Se connecter',
      titlePin: 'Entrer le code PIN',
      titleSuccess: 'Bienvenue!',
      email: 'E-mail',
      emailPlaceholder: 'ton@email.com',
      emailDescription: 'Nous t\'enverrons un code PIN à 6 chiffres pour te connecter',
      pin: 'Code PIN',
      pinPlaceholder: '000000',
      pinDescription: 'Entre le code à 6 chiffres de ton e-mail',
      sentMessage: 'Nous avons envoyé un code PIN à 6 chiffres à',
      successMessage: 'Connexion réussie',
      cancel: 'Annuler',
      back: 'Retour',
      sendCode: 'Envoyer le code',
      verify: 'Vérifier',
      resend: 'Renvoyer le code',
      errorRateLimit: 'Trop de demandes. Attends une minute avant de réessayer.',
      errorInvalid: 'Entre une adresse e-mail valide.',
      errorInvalidPin: 'Code PIN invalide. Réessaie.',
    },
    de: {
      title: 'Anmelden',
      titlePin: 'PIN-Code eingeben',
      titleSuccess: 'Willkommen!',
      email: 'E-Mail',
      emailPlaceholder: 'deine@email.com',
      emailDescription: 'Wir senden dir einen 6-stelligen PIN-Code zum Anmelden',
      pin: 'PIN-Code',
      pinPlaceholder: '000000',
      pinDescription: 'Gib den 6-stelligen Code aus deiner E-Mail ein',
      sentMessage: 'Wir haben einen 6-stelligen PIN an',
      successMessage: 'Erfolgreich angemeldet',
      cancel: 'Abbrechen',
      back: 'Zurück',
      sendCode: 'Code senden',
      verify: 'Überprüfen',
      resend: 'Code erneut senden',
      errorRateLimit: 'Zu viele Anfragen. Bitte warte eine Minute, bevor du es erneut versuchst.',
      errorInvalid: 'Bitte gib eine gültige E-Mail-Adresse ein.',
      errorInvalidPin: 'Ungültiger PIN-Code. Bitte versuche es erneut.',
    },
    it: {
      title: 'Accedi',
      titlePin: 'Inserisci il codice PIN',
      titleSuccess: 'Benvenuto!',
      email: 'Email',
      emailPlaceholder: 'tua@email.com',
      emailDescription: 'Ti invieremo un codice PIN a 6 cifre per accedere',
      pin: 'Codice PIN',
      pinPlaceholder: '000000',
      pinDescription: 'Inserisci il codice a 6 cifre dalla tua email',
      sentMessage: 'Abbiamo inviato un PIN a 6 cifre a',
      successMessage: 'Accesso effettuato con successo',
      cancel: 'Annulla',
      back: 'Indietro',
      sendCode: 'Invia codice',
      verify: 'Verifica',
      resend: 'Reinvia codice',
      errorRateLimit: 'Troppe richieste. Attendi un minuto prima di riprovare.',
      errorInvalid: 'Inserisci un indirizzo email valido.',
      errorInvalidPin: 'Codice PIN non valido. Riprova.',
    },
  };

  const t = translations[currentLang] || translations.en;

  const handleSendCode = async () => {
    setError('');
    setLoading(true);

    try {
      await sendOTP(email);
      setStep('pin');
    } catch (err: any) {
      // Handle specific error cases with translations
      let errorMessage = err.message || 'Failed to send code';

      if (errorMessage.includes('rate limit') || errorMessage.includes('Email rate limit exceeded')) {
        errorMessage = t.errorRateLimit;
      } else if (errorMessage.includes('invalid') || errorMessage.includes('email')) {
        errorMessage = t.errorInvalid;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPin = async () => {
    setError('');
    setLoading(true);

    try {
      await verifyOTP(email, pin);
      // Show success state
      setStep('success');
      // Close modal after showing success for 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      // Handle specific error cases with translations
      let errorMessage = err.message || 'Failed to verify PIN';

      if (errorMessage.includes('invalid') || errorMessage.includes('token') || errorMessage.includes('expired')) {
        errorMessage = t.errorInvalidPin;
      } else if (errorMessage.includes('rate limit')) {
        errorMessage = t.errorRateLimit;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state after animation
    setTimeout(() => {
      setEmail('');
      setPin('');
      setStep('email');
      setError('');
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (step === 'email' && email.trim()) {
        handleSendCode();
      } else if (step === 'pin' && pin.trim()) {
        handleVerifyPin();
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - covers entire screen */}
          <motion.div
            className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm z-[100] transition-colors duration-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          {/* Dialog - centered in viewport */}
          <motion.div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden w-full max-w-md transition-colors duration-300"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 px-6 py-4 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">
                  {step === 'email' ? t.title : step === 'pin' ? t.titlePin : t.titleSuccess}
                </h3>
                {step !== 'success' && (
                  <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors duration-300"
                  >
                    <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="px-6 py-5">
                {step === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    >
                      <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                    </motion.div>
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-lg font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300"
                    >
                      {t.successMessage}
                    </motion.p>
                  </div>
                ) : step === 'email' ? (
                  <>
                    <TextInput
                      autoFocus
                      label={t.email}
                      placeholder={t.emailPlaceholder}
                      value={email}
                      onChange={(e) => setEmail(e.currentTarget.value)}
                      onKeyDown={handleKeyDown}
                      error={error}
                      description={t.emailDescription}
                      type="email"
                      classNames={{
                        input: 'rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 transition-colors duration-300',
                        label: 'text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300',
                        description: 'text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300'
                      }}
                    />
                    <div className="flex justify-end gap-3 mt-6">
                      <motion.button
                        onClick={handleClose}
                        className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors duration-300"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {t.cancel}
                      </motion.button>
                      <motion.button
                        onClick={handleSendCode}
                        disabled={!email.trim() || loading}
                        className="px-4 py-2.5 bg-[#1C7AD6] hover:bg-[#1565C0] text-white text-sm font-medium rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={{ scale: !email.trim() || loading ? 1 : 1.02 }}
                        whileTap={{ scale: !email.trim() || loading ? 1 : 0.98 }}
                      >
                        {loading ? 'Sending...' : t.sendCode}
                      </motion.button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-3 mb-4">
                      <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">
                        {t.sentMessage}
                      </p>
                      <p className="text-sm font-semibold text-[#1C7AD6]">
                        {email}
                      </p>
                    </div>
                    <TextInput
                      autoFocus
                      label={t.pin}
                      placeholder={t.pinPlaceholder}
                      value={pin}
                      onChange={(e) => {
                        const value = e.currentTarget.value.replace(/\D/g, '').slice(0, 6);
                        setPin(value);
                      }}
                      onKeyDown={handleKeyDown}
                      error={error}
                      description={t.pinDescription}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      classNames={{
                        input: 'rounded-lg text-center text-lg tracking-widest font-mono bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 transition-colors duration-300',
                        label: 'text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300',
                        description: 'text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors duration-300'
                      }}
                    />
                    <div className="flex justify-between gap-3 mt-6">
                      <motion.button
                        onClick={() => {
                          setStep('email');
                          setPin('');
                          setError('');
                        }}
                        className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors duration-300"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {t.back}
                      </motion.button>
                      <div className="flex gap-3">
                        <motion.button
                          onClick={handleSendCode}
                          disabled={loading}
                          className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors duration-300 disabled:opacity-50"
                          whileHover={{ scale: loading ? 1 : 1.02 }}
                          whileTap={{ scale: loading ? 1 : 0.98 }}
                        >
                          {t.resend}
                        </motion.button>
                        <motion.button
                          onClick={handleVerifyPin}
                          disabled={pin.length !== 6 || loading}
                          className="px-4 py-2.5 bg-[#1C7AD6] hover:bg-[#1565C0] text-white text-sm font-medium rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          whileHover={{ scale: pin.length !== 6 || loading ? 1 : 1.02 }}
                          whileTap={{ scale: pin.length !== 6 || loading ? 1 : 0.98 }}
                        >
                          {loading ? 'Verifying...' : t.verify}
                        </motion.button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
